import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import { deleteReportFilter } from "../../../../../apis";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import qs from "query-string";
import {
  alpha,
  Box,
  Dialog,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  Typography,
  useTheme,
} from "@mui/material";

export const CustomFilterlist = ({
  savedFilters,
  reloadSavedFilters,
  onApplyFilter,
}) => {
  // Custom hooks
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [t] = useTranslation("report");
  const theme = useTheme();
  const location = useLocation();
  // Initial State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  // Open/close menu
  const handleOpenList = (e) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  // Close if there's not element to show
  useEffect(() => {
    if (anchorEl && savedFilters.length === 0) {
      handleClose();
    }
  }, [savedFilters, anchorEl]);

  // Initial elements are determined
  const currentQuery = location.search.startsWith("?")
    ? location.search.slice(1)
    : location.search;
  // Opens confirmation menu
  const handleOpenConfirm = (e, filter_id) => {
    e.stopPropagation();
    setDeleteConfirmOpen(true);
    setFilterToDelete(filter_id);
  };
  // Request to endpoint to delete selected
  const handleDeleteFilter = async () => {
    setIsLoading(true);
    const saving = enqueueSnackbar(`${t("Filter.saving")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await deleteReportFilter(filterToDelete);
      setFilterToDelete(null);
      reloadSavedFilters();
      enqueueSnackbar(t("Filter.filter_deleted"), { variant: "success" });
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      closeSnackbar(saving);
    }
  };
  // Cancel before submitting
  const handleCancelDeleteFilter = () => {
    setFilterToDelete(null);
    setDeleteConfirmOpen(false);
  };

  return (
    <>
      {/* Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        PaperProps={{
          sx: { border: `${theme.palette.error.main} 1px solid` },
        }}
      >
        <Typography variant="h5" pt={2} textAlign="center" color="error">
          {t("Filter.delete_filter")}
        </Typography>
        <Box px={2}>
          <Typography variant="h5" textAlign="center" color="error.light">
            {t("Filter.are_you_sure_you_want_to_delete_this_filter")}
          </Typography>
          <Typography variant="h6" textAlign="center" color="secondary" pt={1}>
            {t("Filter.this_action_cannot_be_undone")}
          </Typography>
        </Box>
        <DialogActions
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-evenly",
            py: 2,
          }}
        >
          <EGButton
            color="error"
            label={t("Filter.delete")}
            variant="outlined"
            onClick={handleDeleteFilter}
            loading={isLoading}
          />
          <EGButton
            color="secondary"
            label={t("Filter.cancel")}
            onClick={handleCancelDeleteFilter}
            loading={isLoading}
          />
        </DialogActions>
      </Dialog>
      {/* Button to open filter list  */}
      <EGIconButton
        iconName="filter"
        iconColor="info"
        iconColorHover="white"
        colorHover="info"
        title={
          savedFilters.length === 0 ? (
            <>
              {t("ReportsList.my_filters")}:<br />
              {t("Filter.no_saved_filters")}
            </>
          ) : (
            t("ReportsList.my_filters")
          )
        }
        onClick={handleOpenList}
        aria-controls={open ? "reports-filters-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disabled={savedFilters.length === 0}
      />

      {/* Filter List */}
      <Menu
        anchorEl={anchorEl}
        id="reports-filters-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: `drop-shadow(0px 2px 8px ${alpha(
                theme.palette.common.black,
                0.25
              )})`,
              mt: 1.5,
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 10,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <List dense sx={{ p: 0.5 }}>
          <Typography variant="h6" sx={{ mb: 1, mx: 1.5 }} textAlign="center">
            {`${t("Filter.saved_filters")}:`}
          </Typography>
          {savedFilters.map((filter, i) => {
            const urlParams = qs.parse(currentQuery);
            const filterParams = qs.parse(filter.url_origin);

            const isSelected =
              JSON.stringify(urlParams) === JSON.stringify(filterParams);

            return (
              <ListItem
                key={filter.filter_id}
                onClick={() => {
                  onApplyFilter(filter.url_origin);
                }}
                secondaryAction={
                  <EGIconButton
                    iconColor="error"
                    iconName="trash_can"
                    colorHover="error"
                    iconColorHover="white"
                    iconSize={17}
                    onClick={(e) => {
                      handleOpenConfirm(e, filter.filter_id);
                    }}
                  />
                }
                sx={{
                  my: 0.25,
                }}
                disablePadding
              >
                <ListItemButton
                  selected={isSelected}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.blueGray50.main}`,
                  }}
                >
                  <ListItemText
                    primary={`●  ${filter.name}`}
                    primaryTypographyProps={{
                      color: isSelected ? "primary" : "secondary",
                      variant: "h6",
                      mr: 1,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Menu>
    </>
  );
};
